import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { finalize } from "rxjs";
import Swal from "sweetalert2";

import { SolicitudesIaService } from "../solicitudes-ia.service";
import { MatrizRiesgoService } from "../../matriz-riesgos-ia/matriz-riesgo.service";
import { FechaBitacoraPipe } from "../../matriz-riesgos-ia/fecha-bitacora.pipe";
import { EmpleadoService } from "../../empleado/empleado.service";
import { ModalDarComponent } from "../modal-dar/modal-dar.component";
import {
  ESTATUS_SIA,
  ORIGEN_SOLICITUD_IA,
  ORIGEN_SOLICITUD_IA_EVAL,
  SolicitudIA,
  SolicitudIADar,
  SolicitudIAHistorial,
} from "../../shared/model/iso42001/solicitud-ia.model";
import { MatrizEvidencia } from "../../shared/model/iso42001/matriz-riesgo.model";

/**
 * Cómo va la evidencia de la evaluación de UNA solicitud.
 *
 * Es por solicitud y no una sola para la pantalla: quien pide puede tener
 * varias abiertas a la vez, y que una falle no dice nada de las demás.
 */
interface EvidenciaEval {
  abierta: boolean;
  carga: "cargando" | "listo" | "falla";
  lista: MatrizEvidencia[];
  /**
   * Cuál de las consultas es la vigente. Abrir, cerrar y volver a abrir
   * lanza dos; si la primera llega tarde, no debe pisar a la segunda.
   */
  turno: number;
  /** La falla fue la sesión caducada (AUTH-001): reintentar no sirve. */
  sesion?: boolean;
}

/** Las cuatro familias de archivo que la pantalla distingue a la vista. */
type TipoEvidencia = "pdf" | "excel" | "imagen" | "otro";

/** Las dos pestañas: pedir una IA, o ver las que uno ya pidió. */
export type PestanaSolicitudIa = "solicitar" | "mias";

/** Lo que se le cuenta a «Nueva solicitud» cuando la solicitud quedó completa. */
export interface SolicitudIaEnviada {
  idSolicitud: number;
  /** Vacío si la lista no se pudo volver a consultar. */
  folio: string;
  /** Cuántos archivos se subieron con ella. */
  archivos: number;
}

/**
 * Pedir una IA nueva, y ver en qué van las propias.
 *
 * Es la pantalla de quien PIDE. Responder vive en la bandeja de los
 * cinco (gestion-solicitudes-ia), igual que en tickets: quien pide y
 * quien evalúa no miran lo mismo.
 *
 * Lo que NO se pide aquí, a propósito: versión, ciclo de vida, nivel de
 * supervisión, impactos ISO. Eso lo escribe quien evalúa —son análisis,
 * no datos que el solicitante pueda dar— y pedírselos garantizaría
 * basura en el FDS-043.
 *
 * Solo vive dentro de «Nueva solicitud» (12/10/2026, pedido de Norman: un
 * solo módulo para pedir y seguir; ya no tiene ruta propia). El
 * concentrador dice cuál de sus dos vistas se ve (`pestanaActiva`) y se
 * entera de los cambios (`pestanaCambiada`). Al quedar completa la
 * solicitud avisa con `enviada`, para que el concentrador enseñe su paso
 * «Listo». Si faltó guardar algo, pasa a «Mis solicitudes de IA», donde
 * la tarjeta ámbar lo dice y ofrece reintentarlo.
 */
@Component({
  selector: "app-solicitud-ia",
  standalone: true,
  templateUrl: "./solicitud-ia.component.html",
  styleUrls: ["./solicitud-ia.component.css"],
  imports: [CommonModule, FormsModule, MatTooltipModule, ModalDarComponent, FechaBitacoraPipe],
})
export class SolicitudIaComponent implements OnInit {
  cargando = false;
  enviando = false;

  /** Lo que se ve: el formulario, o «Mis solicitudes de IA». */
  pestana: PestanaSolicitudIa = "solicitar";

  /** La pestaña que pide el concentrador. */
  @Input() set pestanaActiva(cual: PestanaSolicitudIa | null) {
    if (cual && cual !== this.pestana) {
      this.pestana = cual;
      this.traeALaVista();
    }
  }

  /**
   * La solicitud que el concentrador pide traer a la vista en «Mis
   * solicitudes de IA»: viene del aviso «espera tu respuesta» de «¿Ya
   * pediste algo?». Puede llegar antes que la lista o que la vista: se
   * atiende cuando están las dos.
   */
  @Input() set destacada(id: number | null) {
    this.porDestacar = id;
    this.traeALaVista();
  }

  /** La que se resalta en la lista. */
  resaltada: number | null = null;
  private porDestacar: number | null = null;

  /** La vista cambió desde dentro (después de enviar). */
  @Output() pestanaCambiada = new EventEmitter<PestanaSolicitudIa>();
  /** La solicitud quedó completa. */
  @Output() enviada = new EventEmitter<SolicitudIaEnviada>();
  /** «Cancelar»: regresar a las tarjetas del concentrador. */
  @Output() volver = new EventEmitter<void>();
  /** Limpiar, Cancelar y Enviar: el concentrador los pone arriba, en su encabezado. */
  @ViewChild("acciones", { static: true }) acciones!: TemplateRef<unknown>;

  mias: SolicitudIA[] = [];

  /**
   * Como fue la ultima consulta de "Mis solicitudes".
   *
   * Sin esto, un fallo de red enseniaba "Todavia no has pedido ninguna
   * IA" —el mismo cartel que ve quien de verdad no ha pedido nada— y sin
   * ningun boton para volver a intentarlo. Es la peor mentira posible en
   * esta pantalla: quien acaba de mandar una cree que se perdio.
   */
  miasCarga: "cargando" | "listo" | "falla" = "listo";
  miasError = "";

  /**
   * Lo que no se pudo guardar DESPUES de crear la solicitud.
   *
   * La solicitud ya existe y tiene folio; lo que falta son los adjuntos o
   * el DAR. Se guardan aqui para poder reintentarlo desde la misma
   * pantalla, que es el unico sitio donde todavia se tienen los archivos:
   * el navegador no los conserva al recargar.
   */
  pendienteDe: number | null = null;
  archivosFallidos: File[] = [];
  /**
   * El DAR que no se pudo guardar, APARTADO del formulario.
   *
   * Aparte y no en darCapturado: si se quedara ahí, el chip "DAR
   * capturado" seguiría colgando del formulario vacío y la siguiente
   * solicitud se mandaría con el DAR de la anterior.
   */
  darPendiente: SolicitudIADar | null = null;
  darSinGuardar = false;
  reintentando = false;
  areas: Array<{ id?: number; area?: string }> = [];

  /** El nombre de quien está dentro: la solicitud va a su nombre. */
  yo = "";

  /**
   * La evidencia de la evaluación de cada solicitud, por id.
   *
   * Se consulta al pulsar «Ver evidencia» y no al cargar la lista: la
   * cuenta ya viene con cada solicitud (evidenciasEval), y pedir los
   * archivos de todas las tarjetas en cada recarga sería una consulta por
   * solicitud para algo que casi nadie abre.
   */
  evidEval: { [idSolicitud: number]: EvidenciaEval } = {};

  /** Los archivos que se están bajando: dos clics no bajan dos copias. */
  private bajando = new Set<number>();

  /* ---------------- El DAR ----------------
     Se puede SUBIR el Excel del FOR-012 o llenarlo aquí. Lo que se
     captura se guarda cuando la solicitud ya existe, porque cuelga de
     ella; hasta entonces vive en memoria. */

  darAbierto = false;
  /** El DAR capturado, todavía sin mandar. */
  darCapturado: SolicitudIADar | null = null;

  /* ---------------- El formulario ---------------- */
  nombreIa = "";
  urlIa = "";
  proposito = "";
  usoPrevisto = "";
  proveedor = "";
  idArea: number | null = null;
  telefonoContacto = "";
  datosSensibles = "";
  fechaRequerida = "";

  /**
   * Hoy, como lo pide el <input type="date"> (yyyy-MM-dd) y con la hora de
   * aquí: «¿Para cuándo la necesitas?» no puede quedar en el pasado.
   */
  readonly hoy = (() => {
    const d = new Date();
    return d.getFullYear() + "-" + ("0" + (d.getMonth() + 1)).slice(-2) + "-" + ("0" + d.getDate()).slice(-2);
  })();

  /** El Excel del DAR, antes de mandarlo. */
  archivos: File[] = [];

  /**
   * Sí o no, y ya.
   *
   * Hubo un tercero, "No lo sé", y se quitó el 11/09/2026: dejaba salir
   * la solicitud con la pregunta que dispara la evaluación de riesgo sin
   * contestar, y quien evalúa se quedaba igual que sin respuesta. Si de
   * verdad no se sabe, se deja en blanco —el campo es opcional— o se
   * pregunta antes de pedirla.
   */
  readonly DATOS = [
    { valor: "NO", texto: "No" },
    { valor: "SI", texto: "Sí" },
  ];

  constructor(
    private service: SolicitudesIaService,
    private evidencias: MatrizRiesgoService,
    private empleados: EmpleadoService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.empleados.consultaAreas().subscribe({
      next: (res: any) => {
        // ConsultaList expone `list`, no `lista`: con el nombre mal el
        // select salia vacio y no habia forma de escoger area.
        const todas: Array<{ id?: number; area?: string; estatus?: string }> =
          (res && res.list) || [];
        // Las de BAJA no se ofrecen: son areas que ya no existen.
        this.areas = todas.filter((a) => !a.estatus || a.estatus === "ACTIVO");
        this.cdr.detectChanges();
      },
      error: () => {},
    });
  }

  /** `despues` corre al terminar la consulta, salga bien o mal. */
  cargar(despues?: () => void): void {
    this.cargando = true;
    this.miasCarga = "cargando";
    this.service.mias().pipe(finalize(() => despues?.())).subscribe({
      next: (res) => {
        this.cargando = false;
        if (res.codigoError) {
          // El aviso va EN la pestania, con su boton, y no en un toast
          // que se va solo: lo que hay que ofrecer aqui es reintentar.
          this.miasCarga = "falla";
          // AUTH-001 viaja en un 200 con codigoError, así que ningún
          // interceptor lo ve: si no se distingue aquí, "Sesión inválida"
          // se lee como un fallo de red y se reintenta en balde.
          this.miasError =
            res.codigoError === "AUTH-001"
              ? "Tu sesión caducó. Vuelve a entrar para ver tus solicitudes."
              : res.mensajeError || "No se pudieron cargar tus solicitudes.";
          this.cdr.detectChanges();
          return;
        }
        this.miasCarga = "listo";
        this.miasError = "";
        this.mias = res.solicitudes || [];
        this.yo = res.yo || "";
        this.cdr.detectChanges();
        this.traeALaVista();
      },
      error: () => {
        this.cargando = false;
        this.miasCarga = "falla";
        this.miasError = "No se pudo conectar con el servidor.";
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Trae a la vista la solicitud pedida: la resalta, la centra en la
   * pantalla y deja el foco en su «Responder», si lo tiene.
   */
  private traeALaVista(): void {
    const id = this.porDestacar;
    if (!id || this.pestana !== "mias" || this.miasCarga !== "listo" || !this.mias.some((s) => s.idSolicitudIa === id)) {
      return;
    }
    this.porDestacar = null;
    this.resaltada = id;
    this.cdr.detectChanges();
    const tarjeta = document.getElementById(`sia-sol-${id}`);
    const sinMovimiento = globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
    tarjeta?.scrollIntoView({ block: "center", behavior: sinMovimiento ? "auto" : "smooth" });
    tarjeta?.querySelector<HTMLButtonElement>(".sia-pendiente button")?.focus({ preventScroll: true });
  }

  verPestana(cual: PestanaSolicitudIa): void {
    this.pestana = cual;
    this.pestanaCambiada.emit(cual);
    this.cdr.detectChanges();
  }

  /**
   * Limpia el formulario sin salir de la pestaña.
   *
   * El DAR capturado se descarta TAMBIÉN. Sin esto se quedaba en
   * memoria y salía colgado de la siguiente solicitud: el FOR-012 de una
   * idea pegado al expediente de otra.
   */
  descartar(): void {
    this.limpia();
    // «Limpiar» deja el formulario en blanco, también el área y el
    // teléfono (13/10/2026). Solo después de ENVIAR se conservan: ver
    // limpia().
    this.idArea = null;
    this.telefonoContacto = "";
    this.darCapturado = null;
    this.cdr.detectChanges();
  }

  /**
   * Quitar el DAR ya capturado, desde su etiqueta. Con pregunta: es lo
   * que más cuesta llenar de toda la pantalla.
   */
  quitaDar(): void {
    if (!this.darCapturado) {
      return;
    }
    Swal.fire({
      title: "¿Descartar el DAR?",
      text: "Se perderá todo lo que capturaste del FOR-012.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Descartar",
      cancelButtonText: "Conservarlo",
    }).then((r) => {
      if (r.isConfirmed) {
        this.darCapturado = null;
        this.cdr.detectChanges();
      }
    });
  }

  /* ---------------- El DAR ---------------- */

  abreDar(): void {
    this.darAbierto = true;
    this.cdr.detectChanges();
  }

  cierraDar(): void {
    this.darAbierto = false;
    this.cdr.detectChanges();
  }

  /**
   * El modal devolvió el DAR. Todavía NO se manda: la solicitud aún no
   * existe y el DAR cuelga de ella. Se guarda en memoria y se envía
   * junto con el alta.
   */
  guardaDar(dar: SolicitudIADar): void {
    this.darCapturado = dar;
    this.darAbierto = false;
    this.aviso("DAR capturado. Se enviará con la solicitud.", "success");
    this.cdr.detectChanges();
  }

  /** Si ya se llenó algo del DAR. */
  get tieneDar(): boolean {
    return this.darCapturado !== null;
  }

  /* ---------------- Archivos ---------------- */

  /**
   * El DAR (FOR-012) es un Excel y solo eso se sube (13/10/2026): entraba
   * un .txt sin avisar. El servidor exige lo mismo (MatrizEvidenciaBuss).
   */
  readonly EXTENSIONES = ".xlsx,.xls";
  /** 10 MB. Más que eso no es una evaluación, es otra cosa. */
  private readonly MAX_BYTES = 10 * 1024 * 1024;

  eligeArchivos(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const nuevos = Array.from(input.files || []);
    for (const f of nuevos) {
      if (!this.esExcel(f.name)) {
        this.aviso(`"${f.name}" no es un Excel: el DAR se sube en .xlsx o .xls.`, "warning");
        continue;
      }
      if (f.size > this.MAX_BYTES) {
        this.aviso(`"${f.name}" pesa más de 10 MB.`, "warning");
        continue;
      }
      this.archivos.push(f);
    }
    // Se limpia el input: sin esto, volver a elegir EL MISMO archivo no
    // dispara el evento change y parece que no pasó nada.
    input.value = "";
    this.cdr.detectChanges();
  }

  /** Si el nombre termina en una de las EXTENSIONES, sin importar mayúsculas. */
  private esExcel(nombre: string): boolean {
    const minusculas = (nombre || "").toLowerCase();
    return this.EXTENSIONES.split(",").some((extension) => minusculas.endsWith(extension));
  }

  quitaArchivo(i: number): void {
    this.archivos.splice(i, 1);
    this.cdr.detectChanges();
  }

  /**
   * Teléfono: solo números y 10 dígitos (29/09/2026, pedido de Norman). Lo
   * que no es número no entra, se teclee o se pegue, y se corta en 10.
   */
  soloDigitos(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const limpio = input.value.replace(/\D/g, "").slice(0, 10);
    if (input.value !== limpio) {
      input.value = limpio;
    }
    this.telefonoContacto = limpio;
  }

  /**
   * Lo que falta para poder pedir. Vacío = listo. Va en el orden en que
   * están los campos en pantalla, para que se llene de arriba abajo.
   */
  get falta(): string {
    if (!this.nombreIa.trim()) {
      return "Falta el nombre de la IA.";
    }
    if (!this.urlIa.trim()) {
      return "Falta el link de la IA.";
    }
    const u = this.urlIa.trim().toLowerCase();
    if (!u.startsWith("http://") && !u.startsWith("https://")) {
      return "El link debe empezar con http:// o https://";
    }
    if (this.proposito.trim().length < 20) {
      return "Cuenta el propósito con un poco más de detalle (al menos 20 caracteres).";
    }
    if (this.usoPrevisto.trim().length < 20) {
      return "Cuenta el uso previsto con un poco más de detalle (al menos 20 caracteres).";
    }
    if (!this.idArea) {
      return "Escoge el área.";
    }
    // Desde el 29/09/2026: proveedor, teléfono (30/09/2026) y fecha
    // obligatorios, el teléfono de 10 dígitos, y el DAR, subido o llenado aquí. El servidor
    // exige lo mismo, salvo el DAR: ese llega DESPUÉS de crear la
    // solicitud, porque cuelga de su número.
    if (!this.proveedor.trim()) {
      return "Falta el proveedor.";
    }
    if (!this.telefonoContacto) {
      return "Falta el teléfono.";
    }
    if (this.telefonoContacto.length !== 10) {
      return "El teléfono lleva 10 dígitos.";
    }
    if (!this.fechaRequerida) {
      return "Falta para cuándo la necesitas.";
    }
    if (this.fechaRequerida < this.hoy) {
      return "La fecha para cuándo la necesitas no puede ser anterior a hoy.";
    }
    if (!this.archivos.length && !this.tieneDar) {
      return "Falta el DAR: súbelo o llénalo aquí.";
    }
    return "";
  }

  pedir(): void {
    // Un segundo clic mientras el primero sigue en camino no manda otra
    // (13/10/2026: con doble clic se creaban dos solicitudes iguales).
    if (this.enviando) {
      return;
    }
    const falta = this.falta;
    if (falta) {
      this.aviso(falta, "warning");
      return;
    }

    // Si de la solicitud anterior quedó algo sin guardar, mandar otra lo
    // borraría: los archivos y el DAR pendientes viven en memoria y esta
    // es la única pantalla desde donde se pueden reintentar. Antes se
    // perdían en silencio.
    if (this.pendienteDe !== null) {
      Swal.fire({
        title: "Todavía falta guardar algo de la anterior",
        text:
          "De " + (this.folioPendiente || "tu última solicitud") + " " +
          this.queFalto() +
          ". Si mandas esta otra, eso se pierde: ya no se podrá reintentar.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Mandar de todos modos",
        cancelButtonText: "Primero lo reintento",
      }).then((r) => {
        if (r.isConfirmed) {
          this.mandaSolicitud();
        }
      });
      return;
    }

    this.mandaSolicitud();
  }

  /** El envío de verdad, ya despejadas las dudas. */
  private mandaSolicitud(): void {
    this.enviando = true;
    // El botón se apaga ya, sin esperar a la siguiente pasada de pantalla.
    this.cdr.detectChanges();
    // Una solicitud nueva empieza sin deudas de la anterior.
    this.pendienteDe = null;
    this.archivosFallidos = [];
    this.darPendiente = null;
    this.darSinGuardar = false;
    const solicitud: SolicitudIA = {
      nombreIa: this.nombreIa.trim(),
      urlIa: this.urlIa.trim(),
      proposito: this.proposito.trim(),
      usoPrevisto: this.usoPrevisto.trim(),
      proveedor: this.proveedor.trim() || undefined,
      idArea: this.idArea!,
      telefonoContacto: this.telefonoContacto.trim() || undefined,
      datosSensibles: this.datosSensibles || undefined,
      fechaRequerida: this.fechaRequerida || undefined,
    };

    this.service.solicitar(solicitud).subscribe({
      next: (res) => {
        // El aviso de éxito va DESPUÉS de comprobar el código de error, no
        // antes: en el alta de tickets sale primero y anuncia como buenas
        // solicitudes que el servidor rechazó.
        if (res.codigoError || !res.idGenerado) {
          this.enviando = false;
          this.aviso(res.mensajeError || "No se pudo crear la solicitud.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.subeArchivos(res.idGenerado);
      },
      error: () => {
        this.enviando = false;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Los archivos se suben DESPUÉS, uno por uno, porque cuelgan del id que
   * acaba de nacer.
   *
   * Los que fallen se GUARDAN, no se anuncian aquí: el aviso lo da
   * termina(), una sola vez y con todo lo que faltó. Antes salía un aviso
   * por cada cosa y el de éxito los tapaba a todos en el mismo instante.
   */
  private subeArchivos(idSolicitud: number): void {
    const cuales = this.archivos.slice();
    if (!cuales.length) {
      this.mandaDar(idSolicitud, 0);
      return;
    }
    let subidos = 0;
    const fallidos: File[] = [];
    const total = cuales.length;
    const siguiente = (i: number) => {
      if (i >= total) {
        this.archivosFallidos = fallidos;
        this.mandaDar(idSolicitud, subidos);
        return;
      }
      const f = cuales[i];
      this.evidencias
        .subirEvidencia(f, ORIGEN_SOLICITUD_IA, idSolicitud, "Evaluación de la IA")
        .subscribe({
          next: (r: any) => {
            if (r && r.codigoError) {
              fallidos.push(f);
            } else {
              subidos++;
            }
            siguiente(i + 1);
          },
          error: () => {
            fallidos.push(f);
            siguiente(i + 1);
          },
        });
    };
    siguiente(0);
  }

  /**
   * El DAR capturado se manda al final, cuando la solicitud ya tiene id.
   *
   * Si falla NO se borra: es media hora de trabajo y es lo único que
   * queda de él. Se marca, y termina() ofrece reintentarlo.
   */
  private mandaDar(idSolicitud: number, subidos: number): void {
    if (!this.darCapturado) {
      this.darSinGuardar = false;
      this.termina(idSolicitud, subidos);
      return;
    }
    const dar = { ...this.darCapturado, idSolicitudIa: idSolicitud };
    this.service.guardarDar(dar).subscribe({
      next: (res) => {
        this.darSinGuardar = !!res.codigoError;
        this.termina(idSolicitud, subidos);
      },
      error: () => {
        this.darSinGuardar = true;
        this.termina(idSolicitud, subidos);
      },
    });
  }

  /**
   * Un solo aviso, con lo que de verdad pasó.
   *
   * Completa, el aviso es el paso «Listo» del concentrador: el botón sigue
   * en «Enviando…» hasta tener el folio. Si faltó algo, el aviso es la
   * tarjeta ámbar de «Mis solicitudes de IA», con «Reintentar»: un aviso
   * flotante encima repetía lo mismo (revisión del 12/10/2026).
   */
  private termina(idSolicitud: number, subidos: number): void {
    const falto = this.archivosFallidos.length || this.darSinGuardar;
    // El formulario queda limpio pase lo que pase: lo que faltó cuelga
    // ahora de la solicitud que ya existe, no del formulario.
    this.pendienteDe = falto ? idSolicitud : null;
    this.darPendiente = this.darSinGuardar ? this.darCapturado : null;
    this.darCapturado = null;
    this.limpia();

    if (!falto) {
      this.enviando = true;
      this.cargar(() => {
        this.enviando = false;
        this.enviada.emit({ idSolicitud, folio: this.folioDe(idSolicitud), archivos: subidos });
      });
      return;
    }

    this.enviando = false;
    // A «Mis solicitudes de IA»: ahí está la solicitud, con su folio, y
    // lo que faltó con su botón para reintentarlo.
    this.verPestana("mias");
    this.cargar();
  }

  /** Lo que quedó sin guardar, escrito para leerse en una línea. */
  queFalto(): string {
    const trozos: string[] = [];
    if (this.archivosFallidos.length) {
      trozos.push(
        this.archivosFallidos.length === 1
          ? `no se pudo subir ${this.archivosFallidos[0].name}`
          : `no se pudieron subir ${this.archivosFallidos.length} archivos`,
      );
    }
    if (this.darSinGuardar) {
      trozos.push("el DAR capturado no se guardó");
    }
    return trozos.join(" y ");
  }

  /** El folio de la solicitud a la que le falta algo, para nombrarla. */
  get folioPendiente(): string {
    return this.folioDe(this.pendienteDe);
  }

  private folioDe(idSolicitud: number | null): string {
    const s = this.mias.find((x) => x.idSolicitudIa === idSolicitud);
    return s?.folio || "";
  }

  /**
   * Volver a subir lo que faltó, contra la solicitud que YA existe.
   *
   * No crea nada nuevo: cuelga de pendienteDe, así que no hay manera de
   * acabar con dos solicitudes iguales por darle dos veces.
   */
  reintentaPendiente(): void {
    if (this.pendienteDe === null || this.reintentando) {
      return;
    }
    const id = this.pendienteDe;
    this.reintentando = true;
    const quedan: File[] = [];
    const cuales = this.archivosFallidos.slice();

    const cierra = () => {
      this.archivosFallidos = quedan;
      if (!quedan.length && !this.darSinGuardar) {
        this.pendienteDe = null;
        this.darPendiente = null;
        this.aviso("Listo: ya quedó todo guardado.", "success");
      } else {
        this.aviso("Todavía " + this.queFalto() + ".", "warning");
      }
      this.reintentando = false;
      this.cargar();
    };

    const conDar = () => {
      if (!this.darSinGuardar || !this.darPendiente) {
        cierra();
        return;
      }
      const dar = { ...this.darPendiente, idSolicitudIa: id };
      this.service.guardarDar(dar).subscribe({
        next: (res) => {
          this.darSinGuardar = !!res.codigoError;
          cierra();
        },
        error: () => {
          this.darSinGuardar = true;
          cierra();
        },
      });
    };

    const siguiente = (i: number) => {
      if (i >= cuales.length) {
        conDar();
        return;
      }
      const f = cuales[i];
      this.evidencias
        .subirEvidencia(f, ORIGEN_SOLICITUD_IA, id, "Evaluación de la IA")
        .subscribe({
          next: (r: any) => {
            if (r && r.codigoError) {
              quedan.push(f);
            }
            siguiente(i + 1);
          },
          error: () => {
            quedan.push(f);
            siguiente(i + 1);
          },
        });
    };
    siguiente(0);
  }

  private limpia(): void {
    this.nombreIa = "";
    this.urlIa = "";
    this.proposito = "";
    this.usoPrevisto = "";
    this.proveedor = "";
    this.datosSensibles = "";
    this.fechaRequerida = "";
    this.archivos = [];
    // Después de enviar, el área y el teléfono NO se limpian: son los
    // mismos para la siguiente solicitud. «Limpiar» sí los borra
    // (descartar).
  }

  /* ---------------- Retirar ---------------- */

  retirar(s: SolicitudIA): void {
    if (s.idSolicitudIa == null) {
      return;
    }
    Swal.fire({
      title: "¿Retirar la solicitud?",
      text: "Dejará de esperar respuesta. Puedes volver a pedirla después.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, retirar",
      cancelButtonText: "No",
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.service.cancelar(s.idSolicitudIa!).subscribe({
        next: (res) => {
          if (res.codigoError) {
            this.aviso(res.mensajeError || "No se pudo retirar.", "error");
            this.cdr.detectChanges();
            return;
          }
          this.aviso("Solicitud retirada.", "success");
          this.cargar();
        },
        error: () => {
          this.aviso("No se pudo conectar con el servidor.", "error");
          this.cdr.detectChanges();
        },
      });
    });
  }

  /* ---------------- Presentación ---------------- */

  puedeRetirar(s: SolicitudIA): boolean {
    return (
      s.estatus === "SOLICITADA" ||
      s.estatus === "EN_EVALUACION" ||
      s.estatus === "INFORMACION_REQUERIDA"
    );
  }

  etiqueta(s: SolicitudIA): string {
    const e = ESTATUS_SIA.find((x) => x.valor === s.estatus);
    return e ? e.texto : s.estatus || "";
  }

  clase(s: SolicitudIA): string {
    const e = ESTATUS_SIA.find((x) => x.valor === s.estatus);
    return e ? e.clase : "";
  }

  /** Qué tiene que hacer el solicitante, si algo. */
  pendiente(s: SolicitudIA): string {
    if (s.estatus === "INFORMACION_REQUERIDA") {
      return "Te están pidiendo más información. Contesta con «Responder».";
    }
    return "";
  }

  /**
   * Rendirse con lo que faltó.
   *
   * La solicitud existe y ya la va a ver alguien: lo que se pierde es el
   * adjunto o el DAR, y eso se puede pedir en la evaluación. Se avisa de
   * lo que se está tirando para que no sea un clic distraído.
   */
  descartaPendiente(): void {
    Swal.fire({
      title: "¿Descartar lo que faltó?",
      text:
        "Se perderá " +
        this.queFalto() +
        ". La solicitud sigue en pie; te lo pedirán durante la evaluación.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Descartar",
      cancelButtonText: "Seguir intentando",
    }).then((r) => {
      if (r.isConfirmed) {
        this.pendienteDe = null;
        this.archivosFallidos = [];
        this.darPendiente = null;
        this.darSinGuardar = false;
        this.cdr.detectChanges();
      }
    });
  }

  /* ---------------- Responder cuando piden información ----------------

     El servidor ya lo permitía —comentar() deja comentar al dueño de la
     solicitud justamente para esto— pero no había ningún sitio en la
     pantalla desde donde hacerlo: la tarjeta decía "entra al detalle y
     responde" y ese detalle no existe para quien pide. La solicitud se
     quedaba parada esperando una respuesta imposible de dar. */

  /** Responder a lo que le pidieron, sin salir de aquí. */
  responde(s: SolicitudIA): void {
    if (!s || s.idSolicitudIa == null) {
      return;
    }
    // Antes de preguntar, se trae la historia para poder enseñar QUÉ le
    // pidieron: contestar sin la pregunta delante es contestar a ciegas.
    this.service.detalle(s.idSolicitudIa).subscribe({
      next: (res) =>
        this.abreRespuesta(
          s,
          res.codigoError ? "" : this.loQuePidieron(res.historial || []),
        ),
      error: () => this.abreRespuesta(s, ""),
    });
  }

  /** El último "falta esto" que escribió quien la evalúa. */
  private loQuePidieron(h: SolicitudIAHistorial[]): string {
    for (let i = h.length - 1; i >= 0; i--) {
      if (
        h[i].estatusNuevo === "INFORMACION_REQUERIDA" &&
        (h[i].comentario || "").trim()
      ) {
        return (h[i].comentario || "").trim();
      }
    }
    return "";
  }

  private abreRespuesta(s: SolicitudIA, pidieron: string): void {
    Swal.fire({
      title: "Responder",
      html: pidieron
        ? '<p style="font-size:12.5px;color:#64748b;margin:0 0 6px;text-align:left">' +
          "Esto es lo que te pidieron:</p>" +
          '<blockquote style="margin:0 0 12px;padding:9px 12px;text-align:left;' +
          'border-left:3px solid #b71c1c;background:#faf7f7;border-radius:6px;' +
          'font-size:13px;color:#33383f">' +
          this.escapa(pidieron) +
          "</blockquote>"
        : undefined,
      input: "textarea",
      inputPlaceholder: "Escribe la información que te piden.",
      inputAttributes: { maxlength: "2000" },
      showCancelButton: true,
      confirmButtonText: "Enviar respuesta",
      cancelButtonText: "Cancelar",
      inputValidator: (v) => (v && v.trim() ? null : "Escribe tu respuesta."),
    }).then((r) => {
      if (r.isConfirmed && r.value) {
        this.mandaRespuesta(s, String(r.value).trim());
      }
    });
  }

  private mandaRespuesta(s: SolicitudIA, texto: string): void {
    this.service.comentar(s.idSolicitudIa!, texto).subscribe({
      next: (res) => {
        if (res.codigoError) {
          this.aviso(res.mensajeError || "No se pudo enviar la respuesta.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.aviso(
          "Respuesta enviada. Quien la evalúa la verá en la historia de la solicitud.",
          "success",
        );
        this.cargar();
      },
      error: () => {
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /** Lo mínimo para meter texto de la base dentro del html de un SweetAlert. */
  private escapa(t: string): string {
    return (t || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  /* ---------------- El resultado de la evaluación ----------------

     Antes la tarjeta sólo enseñaba el motivo en un renglón, con el nombre
     de quien dictaminó colgando al final. Quien pidió la IA no veía
     cuándo se decidió ni con qué se sustentó: tenía que preguntarlo. */

  /** Si ya hay dictamen. Las dos únicas salidas que lo escriben. */
  tieneDictamen(s: SolicitudIA): boolean {
    return s.estatus === "APROBADA" || s.estatus === "RECHAZADA";
  }

  /**
   * "2026-09-23 10:15" → "23/09/2026 a las 10:15".
   *
   * Se arma a mano y no con DatePipe: el servidor la manda con espacio en
   * vez de «T», y ese formato no lo entienden todos los navegadores
   * (Safari da fecha inválida y el pipe revienta la plantilla).
   */
  fechaLegible(f?: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(f || "");
    if (!m) {
      return f || "";
    }
    const dia = `${m[3]}/${m[2]}/${m[1]}`;
    return m[4] ? `${dia} a las ${m[4]}:${m[5]}` : dia;
  }

  /* ---------------- La evidencia de la evaluación ----------------

     Lo que sube quien evalúa para sustentar su dictamen (origen
     SOLICITUD_IA_EVAL, aparte del DAR que subió quien pide). Aquí sólo se
     LEE: subir y quitar es cosa del responsable, desde la gestión. */

  evidAbierta(s: SolicitudIA): boolean {
    const e = s.idSolicitudIa != null ? this.evidEval[s.idSolicitudIa] : undefined;
    return !!e && e.abierta;
  }

  /** En qué va la consulta de esa solicitud; "" si nunca se abrió. */
  evidCarga(s: SolicitudIA): "" | "cargando" | "listo" | "falla" {
    const e = s.idSolicitudIa != null ? this.evidEval[s.idSolicitudIa] : undefined;
    return e ? e.carga : "";
  }

  evidLista(s: SolicitudIA): MatrizEvidencia[] {
    const e = s.idSolicitudIa != null ? this.evidEval[s.idSolicitudIa] : undefined;
    return e ? e.lista : [];
  }

  /** Abrir o cerrar la lista. */
  alternaEvidencia(s: SolicitudIA): void {
    const id = s.idSolicitudIa;
    if (id == null) {
      return;
    }
    const e = this.evidEval[id];
    if (e && e.abierta) {
      e.abierta = false;
      this.cdr.detectChanges();
      return;
    }
    // Se vuelve a pedir cada vez que se abre: mientras la solicitud sigue
    // en evaluación, quien la atiende puede haber subido o quitado algo
    // desde la última vez.
    this.cargaEvidencia(s);
  }

  /** Si la lista no llegó porque la sesión caducó. */
  evidSesion(s: SolicitudIA): boolean {
    const e = s.idSolicitudIa != null ? this.evidEval[s.idSolicitudIa] : null;
    return !!e && !!e.sesion;
  }

  cargaEvidencia(s: SolicitudIA): void {
    const id = s.idSolicitudIa;
    if (id == null) {
      return;
    }
    const previa = this.evidEval[id];
    const turno = (previa ? previa.turno : 0) + 1;
    this.evidEval[id] = { abierta: true, carga: "cargando", lista: [], turno };
    this.cdr.detectChanges();

    this.evidencias.consultarEvidencias(ORIGEN_SOLICITUD_IA_EVAL, id).subscribe({
      next: (res) => {
        const actual = this.evidEval[id];
        if (!actual || actual.turno !== turno) {
          return;
        }
        // Si el servidor la niega, consultar.do contesta 200 con el cuerpo
        // VACÍO (el error va en los encabezados). Sin lista es falla, no
        // "no hay evidencia": decir que no hay cuando sí la hay es justo
        // lo que no puede pasar en un expediente de auditoría.
        if (!res || res.codigoError || !Array.isArray(res.evidencias)) {
          actual.carga = "falla";
          actual.sesion = !!res && res.codigoError === "AUTH-001";
        } else {
          actual.lista = res.evidencias;
          actual.carga = "listo";
        }
        this.cdr.detectChanges();
      },
      error: () => {
        const actual = this.evidEval[id];
        if (!actual || actual.turno !== turno) {
          return;
        }
        actual.carga = "falla";
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * La familia del archivo, por su extensión.
   *
   * Por extensión y NO por el mimeType: «Ver» abre el archivo dentro del
   * origen del COS, y un SVG o un HTML que se colara como "imagen" podría
   * ejecutar código ahí. Sólo las extensiones de esta lista se abren.
   */
  tipoArchivo(e: MatrizEvidencia): TipoEvidencia {
    const n = (e.nombreArchivo || "").toLowerCase();
    if (n.endsWith(".pdf")) {
      return "pdf";
    }
    if (/\.(xlsx|xlsm|xls|csv)$/.test(n)) {
      return "excel";
    }
    if (/\.(png|jpe?g|gif|webp|bmp)$/.test(n)) {
      return "imagen";
    }
    return "otro";
  }

  iconoArchivo(e: MatrizEvidencia): string {
    switch (this.tipoArchivo(e)) {
      case "pdf":
        return "picture_as_pdf";
      case "excel":
        return "table_view";
      case "imagen":
        return "image";
      default:
        return "description";
    }
  }

  /** Lo que se lee junto al nombre: "PDF", "Excel", "Imagen" o la extensión. */
  etiquetaTipo(e: MatrizEvidencia): string {
    switch (this.tipoArchivo(e)) {
      case "pdf":
        return "PDF";
      case "excel":
        return "Excel";
      case "imagen":
        return "Imagen";
      default: {
        const m = /\.([a-z0-9]{1,5})$/i.exec(e.nombreArchivo || "");
        return m ? m[1].toUpperCase() : "Archivo";
      }
    }
  }

  /**
   * Sólo PDF e imágenes tienen «Ver»: son lo único que el navegador
   * enseña bien. Un Excel abierto en una pestaña no se ve, se descarga
   * igual; y la vista previa propia que tuvo la gestión se quitó por no
   * parecerse al archivo (pedido de Norman, 23/09/2026).
   */
  seVe(e: MatrizEvidencia): boolean {
    const t = this.tipoArchivo(e);
    return t === "pdf" || t === "imagen";
  }

  /** El tipo con el que se abre en la pestaña, sacado de la extensión. */
  private mimeParaVer(e: MatrizEvidencia): string {
    const n = (e.nombreArchivo || "").toLowerCase();
    if (n.endsWith(".pdf")) {
      return "application/pdf";
    }
    if (/\.jpe?g$/.test(n)) {
      return "image/jpeg";
    }
    const m = /\.(png|gif|webp|bmp)$/.exec(n);
    return m ? "image/" + m[1] : "";
  }

  /** 1.2 MB, 350 KB... */
  tamanoLegible(bytes?: number): string {
    if (bytes == null || bytes < 0) {
      return "";
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Abrir un PDF o una imagen en otra pestaña.
   *
   * Por HttpClient y no con un enlace al servidor: el enlace no lleva el
   * token y el servidor contestaría 401.
   */
  verEvidencia(e: MatrizEvidencia): void {
    const id = e.idEvidencia;
    const tipo = this.mimeParaVer(e);
    if (id == null) {
      return;
    }
    if (!tipo) {
      this.descargaEvidencia(e);
      return;
    }
    // La pestaña se abre YA, dentro del clic: si se abre al llegar el
    // archivo, el navegador la toma por ventana emergente y la bloquea.
    const pestana = window.open("", "_blank");
    if (pestana) {
      // Lo que se abre no necesita volver a esta pestaña.
      pestana.opener = null;
    }
    this.evidencias.descargarEvidencia(id).subscribe({
      next: (blob: Blob) => {
        // Si el servidor rechaza, descargar.do contesta 200 SIN cuerpo (el
        // error va en los encabezados): la pestaña se quedaría en blanco.
        if (!blob || blob.size === 0) {
          if (pestana) {
            pestana.close();
          }
          this.aviso("No se pudo abrir el archivo.", "error");
          return;
        }
        // Con el tipo de la extensión, no con el que traiga la respuesta:
        // así sólo se abre lo que de verdad es PDF o imagen.
        const url = URL.createObjectURL(new Blob([blob], { type: tipo }));
        if (pestana) {
          pestana.location.href = url;
        } else {
          window.open(url, "_blank", "noopener");
        }
        // Treinta segundos, no en seguida: revocarla antes de que la
        // pestaña termine de cargarla la deja en blanco.
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      },
      error: () => {
        if (pestana) {
          pestana.close();
        }
        this.aviso("No se pudo abrir el archivo.", "error");
      },
    });
  }

  estaBajando(e: MatrizEvidencia): boolean {
    return e.idEvidencia != null && this.bajando.has(e.idEvidencia);
  }

  /** Bajar el archivo con su nombre. El mismo camino, con token. */
  descargaEvidencia(e: MatrizEvidencia): void {
    const id = e.idEvidencia;
    if (id == null || this.bajando.has(id)) {
      return;
    }
    this.bajando.add(id);
    this.cdr.detectChanges();
    this.evidencias.descargarEvidencia(id).subscribe({
      next: (blob: Blob) => {
        this.bajando.delete(id);
        // Vacío = rechazado por el servidor: bajaría un archivo de 0 bytes.
        if (!blob || blob.size === 0) {
          this.aviso("No se pudo descargar el archivo.", "error");
          this.cdr.detectChanges();
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = e.nombreArchivo || "evidencia";
        // Colgado del documento un instante: Firefox ignora el clic en
        // un enlace que no está en la página.
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Un segundo de margen: hay navegadores que arrancan la descarga
        // después del clic, y con la URL ya revocada bajan un archivo vacío.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.cdr.detectChanges();
      },
      error: () => {
        this.bajando.delete(id);
        this.aviso("No se pudo descargar el archivo.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  trackEvidencia = (_: number, e: MatrizEvidencia) => e.idEvidencia;

  trackSolicitud = (_: number, s: SolicitudIA) => s.idSolicitudIa;

  private aviso(texto: string, icono: "success" | "error" | "warning"): void {
    Swal.fire({
      text: texto,
      icon: icono,
      timer: icono === "success" ? 2200 : undefined,
      showConfirmButton: icono !== "success",
    });
  }
}
